import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import CourseSearcherBubble from '../CourseSearcherBubble';
import KnowledgeBubble from '../KnowledgeBubble';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  UserPlus,
  CreditCard,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ChevronRight,
  Building2,
  GraduationCap,
  Shield,
  Crown,
  Bell,
  FormInput,
  Inbox,
  Mail,
  MessageSquare,
  Bot,
  Plug,
  BarChart3,
  Coins,
  Brain,
  Eye,
  EyeOff,
  Headphones,
  Search,
  Globe,
  Archive,
  Briefcase,
  Sparkles,
  FileSpreadsheet,
  Activity as ActivityIcon
} from 'lucide-react';
import TeamChatWidget from '../TeamChatWidget';
import NotificationBell from '../NotificationBell';
import CreditIndicator from '../CreditIndicator';

const vendorNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, navKey: 'nav:dashboard' },
  { path: '/bpo-agent-dashboard', label: 'Dashboard', icon: LayoutDashboard, navKey: 'nav:bpo_agent_dashboard' },
  // Applications - simple link, tabs are inside the page
  { path: '/applications', label: 'Applications', icon: FileText, navKey: 'nav:applications' },
  { path: '/archived', label: 'Archived', icon: Archive, navKey: 'nav:archived' },
  { path: '/report-builder', label: 'Reporting', icon: BarChart3, navKey: 'nav:reports' },
  { path: '/ai-counselor', label: 'AI Counselor', icon: Brain, badge: 'AI', navKey: 'nav:ai_counselor' },
  // Removed per EBN request: Web Page Builder, WhatsApp (Inbox/Flow/AI Agents/Channels/Diagnostics),
  // BPO Services, BPO Support, BPO Applications, Manager Portal, Admissions Dashboard.
  { path: '/settings', label: 'Core Settings', icon: Settings, navKey: 'nav:settings' },
  { path: '/import/courses', label: 'Import Courses', icon: FileSpreadsheet, badge: 'New', navKey: 'nav:import_courses' },
  { path: '/integrations', label: 'Integrations', icon: Plug, navKey: 'nav:integrations' },
  { path: '/form-creator', label: 'Form Creator', icon: FormInput, navKey: 'nav:form_creator' },
  { path: '/organization-settings', label: 'Organization', icon: Building2, navKey: 'nav:organization' },
  { path: '/offices', label: 'Offices', icon: Building2, navKey: 'nav:offices' },
  { path: '/team', label: 'Team', icon: Users, navKey: 'nav:team' },
  { path: '/agents', label: 'Agents', icon: UserPlus, navKey: 'nav:agents' },
  { path: '/agents/pending', label: 'Pending Agents', icon: UserPlus, navKey: 'nav:agents_pending', badgeKey: 'pendingAgents' },
  { path: '/students', label: 'Students', icon: GraduationCap, navKey: 'nav:students' },
  { path: '/notifications', label: 'Notifications', icon: Bell, navKey: 'nav:notifications' },
  // EBN: AI Credits top-up (admin + super-admin only). SMTP/Twilio are super-admin only.
  { path: '/ai-credits', label: 'AI Credits', icon: Sparkles, badge: 'New', navKey: 'nav:ai_credits' },
  { path: '/smtp-settings', label: 'SMTP Settings', icon: Mail, navKey: 'nav:smtp', superAdminOnly: true },
  { path: '/twilio-settings', label: 'Twilio SMS', icon: MessageSquare, navKey: 'nav:twilio', superAdminOnly: true },
  { path: '/billing', label: 'Billing & Credits', icon: CreditCard, navKey: 'nav:billing', superAdminOnly: true },
  // Hidden from main nav but still accessible
  { path: '/leads', label: 'Leads', icon: Users, navKey: 'nav:leads', hidden: true },
  { path: '/web-forms', label: 'Web Forms', icon: Inbox, badge: 'Enquiry', navKey: 'nav:web_forms', hidden: true },
];

const superAdminNavItems = [
  // Super-admin links removed per EBN migration §4.5 — pages no longer exist
];

const DashboardLayout = ({ children, hideMainPadding = false }) => {
  const { user, logout, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [navSearch, setNavSearch] = useState('');
  const isSuperAdmin = user?.is_super_admin || user?.user_type === 'super_admin' || user?.roles?.some(r => r.name === 'Super Admin');
  const isBPOAgent = user?.user_type === 'bpo_agent' || user?.roles?.some(r => r.name === 'BPO Agent');
  // User-controlled hide toggle for the floating AI Counsellor + Team Chat FABs
  // (persists across reloads via localStorage so the preference sticks).
  const [fabsHidden, setFabsHidden] = useState(() => {
    try { return localStorage.getItem('ebn:fabsHidden') === '1'; } catch { return false; }
  });
  const isBPOManager = user?.user_type === 'bpo_region_manager' || user?.roles?.some(r => r.name === 'BPO Region Manager');
  const isBPOTeamMember = user?.user_type === 'bpo_team_member' || user?.roles?.some(r => r.name === 'BPO Team Member');
  
  // BPO unread count
  const [bpoUnreadCount, setBpoUnreadCount] = useState(0);
  
  // EBN pending agents count (super admin + admin)
  const [pendingAgentsCount, setPendingAgentsCount] = useState(0);
  
  useEffect(() => {
    const fetchPendingAgents = async () => {
      try {
        const r = await api().get('/ebn/admin/agents/pending/count');
        setPendingAgentsCount(r.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchPendingAgents();
    const interval = setInterval(fetchPendingAgents, 30000);
    return () => clearInterval(interval);
  }, [api]);
  
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (isSuperAdmin) return;
      try {
        const response = await api().get('/bpo/unread-count');
        setBpoUnreadCount(response.data.unread_count || 0);
      } catch (e) { /* silently fail */ }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [api, isSuperAdmin]);

  // Fetch organization logo
  useEffect(() => {
    const fetchOrgLogo = async () => {
      if (isSuperAdmin) return;
      try {
        const response = await api().get('/organization/profile');
        if (response.data.logo_url) {
          setOrgLogo(response.data.logo_url);
        }
      } catch (error) {
        // Logo not set, use default
      }
    };
    fetchOrgLogo();
  }, [api, isSuperAdmin]);
  const isAdmin = user?.roles?.some(r => r.name === 'Admin');
  
  // Get navigation permissions from user's roles
  const getUserNavPermissions = () => {
    if (isSuperAdmin || isAdmin) return null; // null means show all
    
    const navPerms = new Set();
    if (user?.roles) {
      user.roles.forEach(role => {
        if (role.navigation_permissions) {
          role.navigation_permissions.forEach(p => navPerms.add(p));
        }
      });
    }
    return navPerms;
  };
  
  const navPermissions = getUserNavPermissions();
  
  // BPO Agent specific navigation - limited to their portal items
  const bpoAgentNavItems = [
    { path: '/bpo-agent-dashboard', label: 'Dashboard', icon: LayoutDashboard, navKey: 'nav:bpo_agent_dashboard' },
    { path: '/bpo-agent-applications', label: 'My Applications', icon: FileText, navKey: 'nav:bpo_agent_applications' },
  ];
  
  // BPO Manager specific navigation
  const bpoManagerNavItems = [
    { path: '/bpo-manager-portal', label: 'Manager Portal', icon: Briefcase, navKey: 'nav:bpo_manager_portal' },
  ];
  
  // Filter nav items based on permissions, hidden flag, user role, and search
  const baseNavItems = useMemo(() => {
    if (isSuperAdmin) {
      // EBN: super admin sees the full admin nav (the legacy /super-admin/* pages
      // were deleted in the migration). All items, no permission filtering.
      return vendorNavItems.filter((item) => {
        if (item.groupLabel) return true;
        if (item.hidden) return false;
        if (item.navKey === 'nav:bpo_agent_dashboard') return false;
        return true;
      });
    }
    if (isBPOAgent) return bpoAgentNavItems;
    if (isBPOManager) return bpoManagerNavItems;
    
    // For regular users + the new "Admin" role, filter based on permissions
    // and hide super-admin-only items (SMTP, Twilio, full Billing).
    return vendorNavItems.filter(item => {
      if (item.groupLabel) return true;
      if (item.hidden) return false;
      if (item.navKey === 'nav:bpo_agent_dashboard') return false;
      if (item.superAdminOnly) return false;
      if (!navPermissions) return true;
      return navPermissions.has(item.navKey);
    });
  }, [isSuperAdmin, isBPOAgent, isBPOManager, navPermissions]);
  
  // Apply search filter
  const filteredNavItems = useMemo(() => {
    if (!navSearch.trim()) return baseNavItems;
    const searchLower = navSearch.toLowerCase();
    return baseNavItems.filter(item => 
      !item.groupLabel && (
        item.label.toLowerCase().includes(searchLower) ||
        item.path.toLowerCase().includes(searchLower)
      )
    );
  }, [baseNavItems, navSearch]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const getUserRoleBadge = () => {
    if (isSuperAdmin) {
      return <Badge className="bg-amber-500 hover:bg-amber-500"><Crown className="w-3 h-3 mr-1" />Super Admin</Badge>;
    }
    if (user?.user_type === 'vendor_admin') {
      return <Badge className="bg-blue-500 hover:bg-blue-600"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
    }
    if (user?.roles && user.roles.length > 0) {
      return <Badge variant="secondary">{user.roles[0]?.name || 'Staff'}</Badge>;
    }
    return <Badge variant="outline">Staff</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - EduBridge Network Theme-Aware Design */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo - EduBridge Network Branding (Fixed - Never changes with org logo) */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-border">
            <div className={`flex items-center justify-center w-9 h-9 rounded-xl ${isSuperAdmin ? 'bg-gradient-to-br from-amber-500 to-amber-500' : 'bg-gradient-to-br from-amber-500 to-amber-500'} shadow-lg shadow-amber-500/30`}>
              {isSuperAdmin ? <Crown className="w-5 h-5 text-white" /> : <GraduationCap className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight text-foreground">{isSuperAdmin ? 'Cloud Lab' : 'EduBridge Network'}</h1>
              <p className="text-xs text-muted-foreground">{isSuperAdmin ? 'Super Admin' : 'Education CRM'}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Tenant/Role info */}
          <div className="px-4 py-3 border-b border-border">
            <Link 
              to="/organization-settings" 
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-muted border border-border hover:bg-muted/80 transition-colors"
              data-testid="org-profile-link"
            >
              <Avatar className="w-10 h-10 border-2 border-primary/20">
                {orgLogo ? (
                  <AvatarImage src={orgLogo} alt="Organization Logo" />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-amber-500 to-amber-500 text-white font-bold text-sm">
                  {getInitials(user?.tenant?.company_name || 'My Company')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium truncate block text-foreground">
                  {isSuperAdmin ? 'Cloud Lab Platform' : (user?.tenant?.company_name || 'My Company')}
                </span>
                <span className="text-xs text-muted-foreground">Click to edit profile</span>
              </div>
            </Link>
            <div className="mt-2 px-2">
              {getUserRoleBadge()}
            </div>
          </div>

          {/* Search Navigation */}
          <div className="px-4 py-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search menu..."
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                className="pl-9 h-9 bg-muted/50 border-0 focus-visible:ring-1"
                data-testid="nav-search-input"
              />
              {navSearch && (
                <button 
                  onClick={() => setNavSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Navigation - Theme-Aware Style */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavItems.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No menu items found
                </div>
              ) : (
                filteredNavItems.map((item, idx) => {
                  // Section header — not a link
                  if (item.groupLabel) {
                    return (
                      <div
                        key={`group-${item.groupId || idx}`}
                        className="pt-3 pb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70"
                        data-testid={`nav-group-${item.groupId}`}
                      >
                        {item.groupLabel}
                      </div>
                    );
                  }
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  const indentClass = item.group ? 'ml-2' : '';
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => { setSidebarOpen(false); setNavSearch(''); }}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${indentClass} ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                      {/* EBN: Pending Agents red badge */}
                      {item.badgeKey === 'pendingAgents' && pendingAgentsCount > 0 && (
                        <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white animate-pulse" data-testid="pending-agents-badge">
                          {pendingAgentsCount > 99 ? '99+' : pendingAgentsCount}
                        </span>
                      )}
                      {/* BPO unread count badge */}
                      {bpoUnreadCount > 0 && (item.path === '/bpo-services' || item.path === '/bpo-agent-applications') && (
                        <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                          {bpoUnreadCount > 99 ? '99+' : bpoUnreadCount}
                        </span>
                      )}
                      {item.badge && !(bpoUnreadCount > 0 && (item.path === '/bpo-services' || item.path === '/bpo-agent-applications')) && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {item.badge}
                        </Badge>
                      )}
                      {isActive && !item.badge && !(bpoUnreadCount > 0 && (item.path === '/bpo-services' || item.path === '/bpo-agent-applications')) && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </Link>
                  );
                })
              )}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full px-3 py-2 rounded-xl hover:bg-muted transition-colors">
                  <Avatar className="w-8 h-8 border-2 border-primary/30">
                    <AvatarFallback className="bg-gradient-to-br from-amber-500 to-amber-500 text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'light' ? (
                    <Moon className="w-4 h-4 mr-2" />
                  ) : (
                    <Sun className="w-4 h-4 mr-2" />
                  )}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content - EduBridge Network Theme-Aware */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-background/95 backdrop-blur border-b border-border lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <div className="flex-1" />

          {/* Today's date pill */}
          <div className="hidden sm:flex items-center gap-2 mr-3 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-amber-50 dark:from-slate-800 dark:to-slate-900 border border-amber-200/50 dark:border-slate-700">
            <CalendarIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-300" />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200" data-testid="header-today-date">
              {new Date().toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>

          {/* Role Badge in Header */}
          <div className="hidden md:flex items-center gap-3 mr-4">
            {getUserRoleBadge()}
          </div>

          <div className="flex items-center gap-3">
            {/* Credit Indicator - Hidden for BPO Agents */}
            <CreditIndicator />
            
            {/* Support Icon - Hidden for BPO Agents */}
            {!isBPOAgent && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => navigate('/support')}
                      className="relative"
                      data-testid="support-header-btn"
                    >
                      <Headphones className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Help & Support</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Notification Bell - Hidden for BPO Agents */}
            {!isBPOAgent && <NotificationBell />}
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className={hideMainPadding ? "h-[calc(100vh-4rem)] overflow-hidden" : "p-4 lg:p-6 pb-12 min-h-[calc(100vh-4rem)] bg-background"}>
          {children}
        </main>

        {/* Brand attribution footer — fixed at viewport bottom so always visible */}
        {!hideMainPadding && (
          <footer
            className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/92 backdrop-blur-md"
            data-testid="crm-footer"
          >
            <div className="px-6 py-3.5 flex flex-col sm:flex-row items-center sm:justify-between gap-2 text-[12px]">
              <div className="text-left text-muted-foreground">
                © {new Date().getFullYear()} Edu Bridge Network. All rights reserved.
              </div>
              <nav className="flex flex-wrap items-center gap-x-5 gap-y-1 justify-center sm:justify-end text-muted-foreground">
                <a href="/about"   className="hover:text-foreground hover:underline transition-colors">About Us</a>
                <a href="/privacy" className="hover:text-foreground hover:underline transition-colors">Privacy Policy</a>
                <a href="/terms"   className="hover:text-foreground hover:underline transition-colors">Terms &amp; Conditions</a>
                <a href="/contact" className="hover:text-foreground hover:underline transition-colors">Contact</a>
                <span className="text-muted-foreground" data-testid="crm-footer-powered-by">
                  Powered by{' '}
                  <a
                    href="https://www.cloudlabweb.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-foreground hover:text-amber-500 hover:underline transition-colors"
                    data-testid="crm-footer-powered-by-link"
                  >
                    Cloud Lab Technologies&nbsp;-&nbsp;UK
                  </a>
                </span>
              </nav>
            </div>
          </footer>
        )}
      </div>

      {/* AI Counselor Floating Button - Above Chat */}
      {location.pathname !== '/ai-counselor' && !fabsHidden && (
        <div className="fixed bottom-36 right-6 z-50 flex items-center gap-2">
          <span
            className="hidden sm:inline-flex select-none items-center gap-1 px-2.5 py-1 rounded-full
                       bg-white/95 backdrop-blur border border-amber-200 text-[11px] font-semibold
                       text-slate-800 shadow-sm whitespace-nowrap"
            aria-hidden
          >
            AI Counsellor
          </span>
          <Button
            onClick={() => navigate('/ai-counselor')}
            className="rounded-full w-14 h-14 shadow-lg bg-gradient-to-br from-amber-500 to-amber-500 hover:from-slate-800 hover:to-slate-800"
            data-testid="ai-counselor-fab"
            title="AI Counsellor"
          >
            <Brain className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Team Chat Widget - Hidden for BPO Agents (they have their own BPOAgentChat) */}
      {!isBPOAgent && !fabsHidden && <TeamChatWidget />}

      {/* Small always-on toggle to hide/show the floating action buttons — sits
          in the very bottom-right corner so it never blocks table columns. */}
      <button
        onClick={() => {
          const next = !fabsHidden;
          setFabsHidden(next);
          try { localStorage.setItem('ebn:fabsHidden', next ? '1' : '0'); } catch (_e) { /* localStorage may be blocked */ }
        }}
        title={fabsHidden ? 'Show floating chat & AI counsellor' : 'Hide floating chat & AI counsellor'}
        aria-label={fabsHidden ? 'Show floating buttons' : 'Hide floating buttons'}
        className="fixed bottom-3 right-3 z-[60] w-8 h-8 rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-md flex items-center justify-center text-slate-600 hover:text-[#F5A623] hover:border-[#F5A623] transition"
        data-testid="toggle-fabs-btn"
      >
        {fabsHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>

      {/* Quick Course Searcher floating bubble (r63) — hidden for BPO agents and
          on public/login routes via internal HIDE_ON_PATHS check. */}
      {/* Floating Course Searcher + Knowledge bubbles removed per EBN request
          — they were overlapping the Team Chat widget on the admin main page. */}

      {/* MASTER MIND Knowledgebase bubble (r65) — sits above the Course
          Searcher. Hidden for BPO agents (the component also self-hides
          on public/login routes). */}
      {/* KnowledgeBubble (Master Mind) also removed. */}
    </div>
  );
};

export default DashboardLayout;
